// apps/frontend/src/utils/searchSelect.js
// Debounced searchable-select for a single .newc-form-searchselect wrapper.
// search(query) is injected (built from the customer function via makeSearchFn),
// so the same logic serves the editor preview.
// (The production widget reimplements the same behavior inline — keep in sync.)
const MIN_CHARS = 0;   // 0 = al focus mostra tutte le opzioni (query "")

let uidCounter = 0;
function nextUid(prefix) { uidCounter += 1; return prefix + '-' + uidCounter; }

const DEFAULT_LABELS = {
  searching: 'Ricerca in corso…',
  empty: 'Nessun risultato',
  error: 'Errore nella ricerca',
};

// Normalizza un elemento del risultato in { label, value } stringa, oppure null se
// da scartare. Accetta sia oggetti { label, value } sia primitivi (stringa/numero):
// per un primitivo la stringa diventa sia label sia value.
function normalizeOption(o) {
  if (o == null) return null;
  if (typeof o === 'object') {
    if (o.label == null && o.value == null) return null;
    return { label: String(o.label != null ? o.label : o.value), value: String(o.value != null ? o.value : o.label) };
  }
  const s = String(o);
  return { label: s, value: s };
}

// Costruisce la funzione di ricerca del cliente dal suo corpo (stringa).
// Async: così `await`/fetch funzionano. Normalizza sempre in array {label,value} stringa.
export function makeSearchFn(code) {
  const AsyncFunction = Object.getPrototypeOf(async function () {}).constructor;
  let fn;
  try {
    fn = new AsyncFunction('query', code || 'return [];');
  } catch {
    fn = async () => { throw new Error('searchselect: corpo funzione non valido'); };
  }
  return async (query) => {
    const raw = await fn(query);
    if (!Array.isArray(raw)) return [];
    return raw.map(normalizeOption).filter(Boolean);
  };
}

export function wireSearchSelectField(wrapper, { search, debounceMs = 250, labels } = {}) {
  if (!wrapper || wrapper.dataset.wired === '1') return;
  wrapper.dataset.wired = '1';
  const input = wrapper.querySelector('.newc-form-searchselect-input');
  const hidden = wrapper.querySelector('.newc-form-field[data-type="searchselect"], .newc-array-field[data-type="searchselect"]');
  const results = wrapper.querySelector('.newc-form-searchselect-results');
  if (!input || !hidden || !results) return;

  const L = Object.assign({}, DEFAULT_LABELS, labels || {});
  const lens = wrapper.querySelector('.newc-form-searchselect-lens');
  const spinner = wrapper.querySelector('.newc-form-searchselect-spinner');

  let timer = null;
  let items = [];
  let activeIndex = -1;
  let inFlight = false;
  let pendingQuery = null;
  let selectedLabel = null;
  let shownQuery = null;

  const listboxId = results.id || (results.id = nextUid('newc-ss-listbox'));
  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-autocomplete', 'list');
  input.setAttribute('aria-expanded', 'false');
  input.setAttribute('aria-controls', listboxId);
  results.setAttribute('role', 'listbox');

  const setExpanded = (open) => input.setAttribute('aria-expanded', open ? 'true' : 'false');

  const setIcon = (state) => {
    if (!lens || !spinner) return;
    const reduce = typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const spin = state === 'loading' && !reduce;
    lens.style.display = spin ? 'none' : 'block';
    spinner.style.display = spin ? 'block' : 'none';
  };

  const setActiveDescendant = () => {
    const active = activeIndex >= 0 ? items[activeIndex] : null;
    if (active && active.el.id) input.setAttribute('aria-activedescendant', active.el.id);
    else input.removeAttribute('aria-activedescendant');
  };

  const clearActiveStyles = () => items.forEach((it) => {
    it.el.setAttribute('aria-selected', 'false');
    it.el.style.background = '';
  });

  const setActive = (index) => {
    if (!items.length) { activeIndex = -1; setActiveDescendant(); return; }
    if (index < 0) index = 0;
    if (index > items.length - 1) index = items.length - 1;
    activeIndex = index;
    clearActiveStyles();
    const active = items[activeIndex];
    active.el.setAttribute('aria-selected', 'true');
    active.el.style.background = '#eff6ff';
    setActiveDescendant();
  };

  const hide = () => {
    results.style.display = 'none';
    results.innerHTML = '';
    results.removeAttribute('aria-busy');
    items = [];
    activeIndex = -1;
    shownQuery = null;
    setActiveDescendant();
    setExpanded(false);
  };

  const selectItem = (opt) => {
    if (timer) clearTimeout(timer);
    input.value = opt.label || '';
    hidden.value = opt.value != null ? String(opt.value) : '';
    selectedLabel = opt.label || '';
    hide();
  };

  const statusRow = (text, busy) => {
    results.innerHTML = '';
    if (busy) results.setAttribute('aria-busy', 'true'); else results.removeAttribute('aria-busy');
    items = [];
    activeIndex = -1;
    setActiveDescendant();
    const row = document.createElement('div');
    row.setAttribute('role', 'status');
    row.setAttribute('aria-live', 'polite');
    row.textContent = text;
    row.style.cssText = 'padding:11px 12px;font-size:13px;color:#6b7280;';
    results.appendChild(row);
    results.style.display = 'block';
    setExpanded(true);
  };

  const render = (found) => {
    results.innerHTML = '';
    results.removeAttribute('aria-busy');
    items = [];
    activeIndex = -1;
    if (!found.length) { statusRow(L.empty, false); return; }
    found.forEach((opt) => {
      const el = document.createElement('div');
      el.className = 'newc-form-searchselect-option';
      el.id = nextUid('newc-ss-option');
      el.setAttribute('role', 'option');
      el.setAttribute('aria-selected', 'false');
      el.textContent = opt.label || '';
      el.style.cssText = 'padding:11px 12px;cursor:pointer;font-size:13px;color:#1f2937;border-bottom:1px solid #f3f4f6;min-height:44px;box-sizing:border-box;display:flex;align-items:center;';
      el.addEventListener('mouseover', () => {
        const idx = items.findIndex((it) => it.el === el);
        if (idx !== -1) setActive(idx);
      });
      el.addEventListener('mousedown', (e) => { e.preventDefault(); selectItem(opt); });
      results.appendChild(el);
      items.push({ el, opt });
    });
    results.style.display = 'block';
    setExpanded(true);
    setActiveDescendant();
  };

  const isAlreadyShown = (q) => q === shownQuery && results.style.display === 'block';

  const finishRun = () => {
    inFlight = false;
    if (pendingQuery !== null && !isAlreadyShown(pendingQuery)) {
      const next = pendingQuery;
      pendingQuery = null;
      run(next);
    } else {
      pendingQuery = null;
      setIcon('idle');
    }
  };

  const run = (q) => {
    if (inFlight) { pendingQuery = q; return; }
    inFlight = true;
    setIcon('loading');
    statusRow(L.searching, true);
    Promise.resolve()
      .then(() => search(q))
      .then(
        (found) => {
          try { shownQuery = q; render(found); }
          catch { shownQuery = null; statusRow(L.error, false); }
          finally { finishRun(); }
        },
        () => { shownQuery = null; statusRow(L.error, false); finishRun(); },
      );
  };

  input.addEventListener('focus', () => {
    const q = input.value.trim();
    if (isAlreadyShown(q) || inFlight) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => run(q), 0);
  });

  input.addEventListener('input', () => {
    const q = input.value.trim();
    if (selectedLabel !== null && q === selectedLabel) return;
    selectedLabel = null;
    hidden.value = '';                          // qualsiasi modifica invalida la selezione
    if (timer) clearTimeout(timer);
    if (isAlreadyShown(q)) { setIcon('idle'); return; }
    setIcon('loading');
    timer = setTimeout(() => run(q), debounceMs);
  });

  input.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown') {
      if (!items.length) return;
      e.preventDefault();
      setActive(activeIndex < 0 ? 0 : activeIndex + 1);
    } else if (e.key === 'ArrowUp') {
      if (!items.length) return;
      e.preventDefault();
      setActive(activeIndex < 0 ? items.length - 1 : activeIndex - 1);
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && items[activeIndex]) { e.preventDefault(); selectItem(items[activeIndex].opt); }
    } else if (e.key === 'Escape') {
      if (results.style.display === 'block') { e.preventDefault(); hide(); }
    }
  });

  input.addEventListener('blur', () => { setTimeout(hide, 150); });

  // Se il campo è già a fuoco quando lo agganciamo (nella preview/widget il wiring è
  // innescato dal focus stesso), apri subito il dropdown con la query corrente.
  // In jsdom activeElement è il body → nei test l'apertura iniziale NON parte al wiring.
  if (typeof document !== 'undefined' && document.activeElement === input) {
    run(input.value.trim());
  }
}
